/**
 * Vessel Settings Audit Field Catalogue
 *
 * FT-515 round 7: single source of truth for which vessel-settings fields
 * are meaningful to show a user in the audit trail, and what to call them.
 *
 * Root cause of the round-7 bug: VesselSettingsView.js's audit diff compared
 * a FULL live vessel snapshot (beforeSnapshotRef — every field the vessel
 * object happens to carry, including internal/sync bookkeeping like
 * `vesselId`, `access_level`, `_sourceModule`, `_needsCloudCheck`) against a
 * SCHEMA-MAPPED "after" object (finalVesselUpdates, built by
 * mapConfigToVessel from only the known form fields). Any bookkeeping field
 * present in "before" but absent from the schema-mapped "after" registered
 * as a spurious "changed to (empty)" row. VesselSettingsHistoryView.js then
 * rendered every one of those rows verbatim with no label and no filtering.
 *
 * This module is imported by BOTH:
 *  - VesselSettingsView.js (write time) — keeps bookkeeping fields out of the
 *    diff that gets logged in the first place, fixing the root cause for all
 *    NEW entries.
 *  - VesselSettingsHistoryView.js (display time) — the safety net that also
 *    strips bookkeeping fields out of ALREADY-STORED entries (including
 *    legacy entries written before this fix existed, or anything written by
 *    a future code path we haven't audited), and supplies human-readable
 *    labels.
 *
 * Having one shared module instead of two independent lists (as before —
 * VesselSettingsView.js had its own VESSEL_SETTINGS_AUDIT_IGNORE set, and
 * VesselSettingsHistoryView.js had its own separate FIELD_LABELS with no
 * exclusion logic at all) is the point: write-time and display-time must
 * agree on what counts as "internal", or they drift apart again next round.
 *
 * Field list cross-checked field-by-field against
 * src/config/vessel.schema.js's getAllVesselFields() — the schema-driven
 * mapper's own single source of truth for "every real vessel field" (see
 * vesselConfigMapper.js, which already uses getAllVesselFields() to build
 * both mapVesselToConfig and mapConfigToVessel) — PLUS `vesselDescription`,
 * which StepVesselIdentity.js's wizard form collects but which is NOT
 * currently in getAllVesselFields() (a separate, pre-existing schema gap —
 * out of scope to fix here, but the field is real and user-facing, so it
 * still needs a clean label rather than showing raw or being silently
 * dropped).
 *
 * Deliberately NOT a strict allow-list for exclusion: an unrecognised-but-
 * real field falls back to showing its raw key (ugly, but still visible —
 * see labelForVesselSettingsField) rather than vanishing, because silently
 * dropping a genuine change from a compliance audit trail is a worse failure
 * mode than an unpretty label. Only fields we POSITIVELY KNOW are internal
 * bookkeeping (named below, or underscore-prefixed by this codebase's own
 * convention for private/internal fields) are excluded from display.
 */

// Fields that are internal/bookkeeping and must NEVER be shown to a user as
// an "audit change" — either because they're not something a user edits
// (sync/internal plumbing, IDs), or because they're a legacy mirror of
// another field already covered under its canonical name (showing both would
// just double-log the same edit under two different keys).
const VESSEL_SETTINGS_NON_DISPLAY_FIELDS = new Set([
  // IDs / bookkeeping
  'id', 'vesselId', 'orgId', 'organizationId', 'access_level',
  // Sync / cache plumbing
  'syncedAt', 'lastSyncedAt', 'syncedFromCloud', '_isArray', '_internal',
  '_sourceModule', '_needsCloudCheck',
  // Timestamps that change on every save regardless of whether anything the
  // user actually edited changed
  'createdAt', 'updatedAt',
  // Legacy mirror of `category` (see LEGACY_FIELD_MAPPINGS in
  // src/config/vessel.schema.js) — identical value, would double-log.
  'areaCategory',
]);

/**
 * FT-515 round 8: `vesselName`, `vesselType`, `homePort` are also legacy
 * mirrors of their canonical fields (name/type/port respectively — see
 * LEGACY_FIELD_MAPPINGS in src/config/vessel.schema.js, same pattern as
 * `areaCategory` above), but they are worse than a harmless double-log, so
 * they get their OWN set rather than joining VESSEL_SETTINGS_NON_DISPLAY_FIELDS:
 *
 * getCurrentVessel (VesselContext.js) deliberately deletes `vesselName`/
 * `vesselType` from the live vessel object it hands back as the audit diff's
 * "before" snapshot (`homePort` is NOT deleted the same way — round 9 note:
 * once a vessel has been saved at least once, `homePort` IS present in
 * "before" too, since mapConfigToVessel writes it into vesselSettings and
 * getCurrentVessel merges settings in; it's only absent before the FIRST
 * save) — while mapConfigToVessel
 * (vesselConfigMapper.js) ALWAYS re-adds all three to "after" as mirrors of
 * name/type/port. That means a diff that doesn't exclude them sees
 * "absent" -> "present" on EVERY save, even one where the user changed
 * nothing at all, and would log it as a false "changed to X" row for a field
 * that was never really touched independently of its canonical source field
 * (which IS still correctly compared, under its own key).
 *
 * This set is used ONLY by computeVesselSettingsDiff's write-time decision
 * (VesselSettingsView.js) about whether a field on a NEW save counts as
 * "changed" — unlike VESSEL_SETTINGS_NON_DISPLAY_FIELDS, it is deliberately
 * NOT consulted when filtering what to show for ALREADY-STORED entries.
 * Two existing regression tests (VesselSettingsHistoryView.ft515.test.js,
 * VesselSettingsHistoryView.ft515-round3.test.js) seed a legacy-format
 * stored entry keyed `vesselName` with a genuine before/after rename and
 * assert it still renders, labelled "Vessel name" — folding `vesselName`
 * into the shared display-time filter would silently drop that already-
 * written, genuinely-informative entry, which is exactly the failure mode
 * the round-7 module comment above warns against. The bug this round fixes
 * is new false entries being WRITTEN, not how old ones already in storage
 * are read back.
 */
export const VESSEL_SETTINGS_MIRROR_FIELDS_EXCLUDED_AT_WRITE_TIME = new Set([
  'vesselName',
  'vesselType',
  'homePort',
]);

/**
 * True if `key` is a known-internal / bookkeeping field that should never be
 * surfaced to a user — either one of the named fields above, or following
 * this codebase's underscore-prefix convention for internal/private fields
 * (e.g. `_sourceModule`, `_needsCloudCheck`). The underscore rule also
 * catches internal fields we haven't named explicitly (future-proofing
 * without needing to enumerate every possible one).
 *
 * @param {string} key
 * @returns {boolean}
 */
export const isNonDisplayVesselSettingsField = (key) =>
  typeof key === 'string' && (key.startsWith('_') || VESSEL_SETTINGS_NON_DISPLAY_FIELDS.has(key));

/**
 * Human-readable labels for every real, user-meaningful vessel-settings
 * field — cross-checked against src/config/vessel.schema.js's
 * getAllVesselFields() for completeness (see module comment above for the
 * one known gap, `vesselDescription`).
 *
 * An entry NOT in this map is not necessarily hidden — see
 * isNonDisplayVesselSettingsField above for the (separate) exclusion list.
 * A real-but-unlabelled field just falls back to its raw key via
 * labelForVesselSettingsField, which is intentional (see module comment).
 */
export const VESSEL_SETTINGS_FIELD_LABELS = {
  // Identity
  name: 'Vessel name',
  vesselName: 'Vessel name',
  type: 'Vessel type',
  vesselType: 'Vessel type',
  vesselDescription: 'Vessel description',
  imo: 'IMO number',
  flag: 'Flag state',
  port: 'Port of registry',
  homePort: 'Port of registry',
  callSign: 'Call sign',
  mca: 'MCA code',

  // Classification / operating profile
  category: 'WBC3 category',
  operatingStatus: 'Operating status',
  operatingHours: 'Operating hours',
  internationalVoyages: 'International voyages',
  maxVoyageDistance: 'Max voyage distance',
  isUnmanned: 'Is unmanned',
  waterCategory: 'Water category',
  inlandRegulatoryPath: 'Inland regulatory path',

  // Dimensions / build
  lengthOverall: 'Length overall (m)',
  beam: 'Beam (m)',
  draft: 'Draft (m)',
  grossTonnage: 'Gross tonnage',
  age: 'Vessel age (years)',
  yearBuilt: 'Year built',

  // Capacity / persons
  maxPersons: 'Max persons onboard',
  passengerCapacity: 'Passenger capacity',
  crewCount: 'Crew count',
  crewCapacity: 'Crew capacity',
  carriesVulnerablePassengers: 'Carries vulnerable passengers',
  operatesAtNight: 'Operates at night',
  allPersonsWearLifejackets: 'All persons wear lifejackets',

  // Machinery / propulsion
  engineType: 'Engine type',
  enginePower: 'Engine power (kW)',
  enginePosition: 'Engine position',
  numberOfEngines: 'Number of engines',
  propulsionType: 'Propulsion type',
  propulsionConfiguration: 'Propulsion configuration',
  driveType: 'Drive type',
  steeringType: 'Steering type',
  steeringPosition: 'Steering position',
  steeringControlType: 'Steering control type',
  engineStartMethod: 'Engine start method',
  hasOilLubricatedShaft: 'Has oil-lubricated shaft',
  hasWorkboatPilotEndorsement: 'Has workboat pilot endorsement',
  hasEmergencySteering: 'Has emergency steering',
  hasShorepower: 'Has shore power',
  towing_capable: 'Towing capable',
  engineer_onboard: 'Engineer onboard',
  power_critical_systems: 'Power-critical systems',
  bilge_auto: 'Automatic bilge pump',
  hasMachinerySpace: 'Has machinery space',
  hasDecking: 'Has decking',
  hasWheelhouse: 'Has wheelhouse',

  // Accommodation / galley
  hasAccommodation: 'Has accommodation',
  hasEnclosedSpaces: 'Has enclosed spaces',
  enclosed_accommodation_present: 'Enclosed accommodation present',
  cabin_accommodation: 'Cabin accommodation',
  hasGalley: 'Has galley',
  galleyHeatSource: 'Galley heat source',
  hasCooking: 'Has cooking facilities',
  hasHeating: 'Has heating',
  hasOpenFlame: 'Has open flame',
  hasGasAppliance: 'Has gas appliance',
  hasGasAppliances: 'Has gas appliances',
  hasLiquidFuelAppliance: 'Has liquid-fuel appliance',
  hasLPGSystem: 'Has LPG system',
  hasLithiumBatteries: 'Has lithium batteries',
  hasConcentratedElectrical: 'Has concentrated electrical system',
  openFlameProtection: 'Open-flame protection',
  engineBoxProtection: 'Engine box protection',

  // Risk / environmental
  hasExhaustLeakRisk: 'Has exhaust leak risk',
  hasGasLeakRisk: 'Has gas leak risk',
  hasFireRiskArea: 'Has fire risk area',
  gasAppliances: 'Gas appliances',
  hasProhibitedDischargeArea: 'Has prohibited discharge area',
  hasIMOSewageSystem: 'Has IMO sewage system',
  oilCapacityLitres: 'Oil capacity (litres)',

  // Fire-fighting / safety equipment
  fixedFFE: 'Fixed fire-fighting equipment',
  fixedSystems: 'Fixed fire systems',
  isPracticable: 'Fire buckets practicable',
  deemedNecessary: 'Fire buckets deemed necessary',
  companyRequiresBuckets: 'Company requires fire buckets',
  lifejacketType: 'Lifejacket type',
  lsaEquipment: 'LSA equipment',
  mobEquipment: 'MOB equipment',
  hasRescueBoat: 'Has rescue boat',
  hasFastRescueBoat: 'Has fast rescue boat',
  rescue_boat_fitted: 'Rescue boat fitted',
  hasImmersionSuits: 'Has immersion suits',
  hasOpenLiferaft: 'Has open liferaft',
  hasLineThrowing: 'Has line-throwing apparatus',
  lifeboat_fitted: 'Lifeboat fitted',
  liferafts_fitted: 'Liferafts fitted',
  liferaft_capacity: 'Liferaft capacity',
  hru_fitted: 'Hydrostatic release unit fitted',
  hruType: 'HRU type',
  hasStabilityBooklet: 'Has stability booklet',
  hasSART: 'Has SART',
  hasGrabBag: 'Has emergency grab bag',

  // Navigation / communications
  gmdssSeaArea: 'GMDSS sea area',
  seaArea: 'Sea area',
  seaAreaA1: 'Sea area A1',
  seaAreaA2: 'Sea area A2',
  seaAreaA3: 'Sea area A3',
  seaAreaA4: 'Sea area A4',
  hasRadar: 'Has radar',
  hasNavtex: 'Has NAVTEX',
  hasMF: 'Has MF radio',
  hasAIS: 'Has AIS',
  hasLiferaft: 'Has liferaft',
  navigationEquipment: 'Navigation equipment',
  communicationEquipment: 'Communication equipment',
  electricalSystems: 'Electrical systems',
  engineeringSystems: 'Engineering systems',
  operationalEquipment: 'Operational equipment',

  // Equipment / operations (general)
  equipment: 'Equipment list',
  operations: 'Operation types',
  optionalEquipment: 'Optional equipment',
  specialEquipment: 'Special equipment',
  companyRequiredDrills: 'Company-required drills',
  equipmentCheckSettings: 'Equipment check settings',

  // Admin / certification
  certificates: 'Certificates',
  timezone: 'Vessel timezone',
};

/**
 * Human-readable label for a vessel-settings field key, falling back to the
 * raw key itself for anything not in the catalogue above (intentional — see
 * module comment: a real field that hasn't been added here yet should still
 * be visible, just unpretty, not invisible).
 *
 * @param {string} key
 * @returns {string}
 */
export const labelForVesselSettingsField = (key) => VESSEL_SETTINGS_FIELD_LABELS[key] || key;
